import React, { useMemo } from 'react';
import { Box, Divider, Flex, TextV2 } from '@withjoy/joykit';
import type { AccommodationListingDTO, AccommodationsConsoleState } from '@shared/types/Accommodations.types';
import { useAccommodationRedirectUrl, createUtmParametersForAccommodationsConsolePage } from '@apps/stays/AccommodationsConsole/hooks/useAccommodationRedirectUrl';
import { getSelectedRedirectTelemetryName } from '@shared/utils/extractHotelRedirects';
import { useSafeUrlParser } from '@shared/hooks/useSafeUrlParser';
import { useAcquisitionAwareUtm } from '@shared/hooks/useAcquisitionAwareUtm';
import { useAccommodationsListingMobileLayout } from '../../../hooks/useAccommodationsListingMobileLayout';

import { ListingPhotos } from './ListingPhotos';
import { HeaderBlock } from './HeaderBlock';
import { TravelLogistics } from './TravelLogistics';
import { CostBreakdown } from './CostBreakdown';
import { HotelCTA } from './HotelCTA';
import { CouplesPick } from './CouplesPick';
import { useTranslation } from '@shared/core';
import { useEventLocaleFormatter } from '@localization/hooks/useEventLocaleFormatter';
import { UserNote } from './UserNote';
import { useListingsPanelTelemetry } from '../ListingsPanel.telemetry';
import { useEventCallback } from '@shared/utils/hooks/useEventCallback';
import { SoldOutTag } from '@shared/components/SoldOutTag/SoldOutTag';
import { useAccommodationMapListSyncApi } from '../../AccommodationMapListSync/AccommodationMapListSync';
import { OtaProviderName } from './OtaProviderName';
import { getValidCoordinates } from '@shared/core/places';
import { ListingEntryMobileV2 } from './ListingEntryMobileV2';
import { createDateFromUnformattedString } from '@shared/utils/dateHelpers';
import { SOLD_OUT_CTA_ALLOWED_SOURCES } from '@shared/constants/accommodations';
import type { HotelSearchSource } from '@shared/constants/accommodations';
import { withWindow } from '@shared/utils/withWindow';
import { DynamicMargin } from '@apps/stays/EventAccommodations/routes/shared/hotelOfferDiagnostics/DynamicMargin';
import { UserRating } from '@apps/stays/components/Ratings';
import { pxToRem } from '@withjoy/joykit/theme';

const Container = ({ children, id, joyPlaceId }: { children: React.ReactNode; id?: string; joyPlaceId: Maybe<string> }) => {
  const { onHoverStart, onHoverEnd } = useAccommodationMapListSyncApi();

  const handleMouseEnter = useEventCallback(() => {
    if (joyPlaceId) {
      onHoverStart(joyPlaceId);
    }
  });

  const handleMouseLeave = useEventCallback(() => {
    if (joyPlaceId) {
      onHoverEnd();
    }
  });

  return (
    <Flex
      id={id}
      flexDirection={{ _: 'column', sm2: 'row' }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      borderRadius={{ _: 0, sm2: pxToRem(10) }}
      border={{ _: 'none', sm2: '1px solid' }}
      borderColor={{ _: 'transparent', sm2: 'mono3' }}
      overflow={{ _: 'visible', sm2: 'hidden' }}
    >
      {children}
    </Flex>
  );
};

const ContentPanel = ({ children }: { children: React.ReactNode }) => {
  return (
    <Flex
      flexDirection="column"
      width={'100%'}
      gap={{ _: 4, sm2: 0 }}
      paddingY={{ _: 4, sm2: pxToRem(12) }}
      paddingLeft={{ _: 0, sm2: pxToRem(16) }}
      paddingRight={{ _: 0, sm2: pxToRem(16) }}
    >
      {children}
    </Flex>
  );
};

const LogisticsAndCostBlock = ({ children }: { children: React.ReactNode }) => {
  return (
    <Flex
      flexDirection={{ _: 'column', sm2: 'row' }}
      gap={{ _: 4, sm2: 0 }}
      marginTop={{ _: 0, sm2: 6 }}
      justifyContent={{ _: 'flex-start', sm2: 'space-between' }}
      alignItems={{ _: 'none', sm2: 'center' }}
      width={{ _: '100%', sm2: 'auto' }}
    >
      {children}
    </Flex>
  );
};

const CutoffDate = ({ cutoffDate }: { cutoffDate: string }) => {
  const { t } = useTranslation('accommodationsConsole');
  const listingsPanelTrans = t('listingsPanel');
  const { formatDate } = useEventLocaleFormatter();
  const formattedCutoffDate = formatDate(cutoffDate, 'MMMM do, yyyy') ?? '';

  return (
    <Flex justifyContent={'flex-end'}>
      <TextV2
        typographyVariant="label1"
        color={'mono10'}
      >
        {listingsPanelTrans.cutoffDate({ cutoffDate: formattedCutoffDate })}
      </TextV2>
    </Flex>
  );
};

type ListingEntryProps = Readonly<{
  listing: AccommodationListingDTO;
  state: AccommodationsConsoleState;
  noOfNights: number;
  sourceName: HotelSearchSource;
  showRoomBlockNoteAndDate: boolean;
  showAddedTag: boolean;
  showCouplesPicksTag: boolean;
  isAdmin: boolean;
}>;

const ListingEntryInner = (props: ListingEntryProps) => {
  const { listing, state, noOfNights, sourceName, showRoomBlockNoteAndDate, showAddedTag, showCouplesPicksTag, isAdmin } = props;
  const { variant, shouldUseNewMobileLayout, isMobileOrTablet } = useAccommodationsListingMobileLayout();
  const isDesktopLayout = !isMobileOrTablet;
  const getRedirectUrl = useAccommodationRedirectUrl(state, sourceName);
  const redirectUrl = getRedirectUrl(listing);
  const telemetry = useListingsPanelTelemetry();
  const { tq } = useTranslation('accommodationsConsole');
  const parseUrl = useSafeUrlParser();
  // Same resolution the redirect URL uses, so the Amplitude event and the URL can't disagree.
  const mergedUtm = useAcquisitionAwareUtm(createUtmParametersForAccommodationsConsolePage(sourceName));

  const selectedRedirectTelemetryName = useMemo(() => getSelectedRedirectTelemetryName(listing.redirectMechanisms), [listing.redirectMechanisms]);

  const handleSeeAvailability = useEventCallback(() => {
    telemetry.seeAvailabilityClicked({ joyPlaceId: listing.joyPlaceId || '', hotelName: listing.name });

    if (redirectUrl) {
      const urlInfo = parseUrl(redirectUrl, 'ListingEntry.handleSeeAvailability');
      if (urlInfo) {
        telemetry.hotelBookingRedirect({
          joyPlaceId: listing.joyPlaceId || '',
          hotelName: listing.name,
          destinationURL: redirectUrl,
          destinationDomain: urlInfo.hostname,
          destinationName: selectedRedirectTelemetryName,
          ...mergedUtm,
        });
      }
    }
  });

  const isSoldOut = listing.type === 'standardHotel' && listing.isSoldOutForSelectedDates;
  const shouldShowCTAForSoldOut = SOLD_OUT_CTA_ALLOWED_SOURCES.includes(sourceName);
  const shouldShowHotelCta =
    (isSoldOut && shouldShowCTAForSoldOut) || (redirectUrl && ((listing.type === 'standardHotel' && !listing.isSoldOutForSelectedDates) || listing.type === 'roomBlock'));

  const handlePhotoClick = useEventCallback((e: React.MouseEvent) => {
    // Carousel controls stop propagation, so if this handler fires, it's safe to navigate
    // Only navigate if clicking on the image area (not on interactive carousel elements)
    const target = e.target as HTMLElement;
    const isCarouselControl = target.closest('button, [role="button"], [data-carousel-control]');
    if (!isCarouselControl && !isSoldOut && redirectUrl) {
      handleSeeAvailability();
      withWindow(global => global.open(redirectUrl, '_blank'));
    }
  });

  const pointOfInterest = useMemo(() => {
    const location = getValidCoordinates(state.circularRegionSearch);
    if (!location) return null;

    let displayName = state.searchLocation || tq`venue`;
    if (state.searchLocation === state.venue.name) displayName = tq`venue`;

    return { displayName, location };
  }, [state.circularRegionSearch, state.searchLocation, state.venue.name, tq]);

  if (shouldUseNewMobileLayout) {
    return (
      <ListingEntryMobileV2
        state={state}
        listing={listing}
        variant={variant}
        noOfNights={noOfNights}
        sourceName={sourceName}
        showRoomBlockNoteAndDate={showRoomBlockNoteAndDate}
      />
    );
  }

  return (
    <Container
      id={`listing-${listing.joyPlaceId}`}
      joyPlaceId={listing.joyPlaceId}
    >
      <Box
        onTouchEnd={e => e.stopPropagation()}
        onTouchMove={e => e.stopPropagation()}
        onTouchStart={e => e.stopPropagation()}
        onClick={handlePhotoClick}
        cursor={!isSoldOut && redirectUrl ? 'pointer' : 'default'}
      >
        <ListingPhotos
          listing={listing}
          isMobileOrTablet={!isDesktopLayout}
          showAddedTag={showAddedTag}
        />
      </Box>
      <Box
        width={'100%'}
        {...(!isSoldOut && redirectUrl ? { as: 'a', href: redirectUrl, target: '_blank', onClick: handleSeeAvailability } : {})}
      >
        <Flex
          flexDirection="column"
          width={'100%'}
          height={'100%'}
          justifyContent={'space-between'}
        >
          <ContentPanel>
            {listing.isPickedByCouple && (
              <CouplesPick
                listing={listing}
                isAdmin={isAdmin}
                showCouplesPicksTag={showCouplesPicksTag}
              />
            )}
            <HeaderBlock
              listing={listing}
              pointOfInterest={pointOfInterest}
              isMobileOrTablet={!isDesktopLayout}
              redirectUrl={redirectUrl}
            />
            <OtaProviderName otaProvider={listing.otaProvider} />
            {listing.type === 'roomBlock' && listing.userNote && showRoomBlockNoteAndDate && <UserNote note={listing.userNote} />}
            {!isDesktopLayout && <Divider marginY={3} />}
            {isDesktopLayout ? (
              <Flex
                flexDirection="row"
                justifyContent="space-between"
                alignItems="flex-start"
                marginTop={4}
              >
                <Flex
                  flexDirection="column"
                  rowGap={3}
                >
                  {pointOfInterest && (
                    <TravelLogistics
                      pointOfInterest={pointOfInterest}
                      listing={listing}
                    />
                  )}
                  {listing.userRating && (
                    <UserRating
                      rating={listing.userRating.ratingValue}
                      reviewCount={listing.userRating.ratingCount}
                    />
                  )}
                </Flex>
                <Flex
                  flexDirection="column"
                  gap={5}
                >
                  {listing.type === 'roomBlock' && showRoomBlockNoteAndDate && <CutoffDate cutoffDate={listing.cutoffDate} />}
                  {listing.pricingInfo.isLoading ? (
                    <CostBreakdown.Skeleton />
                  ) : listing.pricingInfo.values ? (
                    <CostBreakdown
                      occupancies={state.occupancies}
                      pricingInfo={listing.pricingInfo.values}
                      noOfNights={noOfNights}
                    />
                  ) : null}
                </Flex>
              </Flex>
            ) : (
              <LogisticsAndCostBlock>
                {pointOfInterest && (
                  <TravelLogistics
                    pointOfInterest={pointOfInterest}
                    listing={listing}
                  />
                )}
                {listing.userRating && (
                  <UserRating
                    rating={listing.userRating.ratingValue}
                    reviewCount={listing.userRating.ratingCount}
                  />
                )}
                <Flex
                  flexDirection={'column'}
                  gap={5}
                >
                  {listing.type === 'roomBlock' && showRoomBlockNoteAndDate && <CutoffDate cutoffDate={listing.cutoffDate} />}
                  {listing.pricingInfo.isLoading ? (
                    <CostBreakdown.Skeleton />
                  ) : listing.pricingInfo.values ? (
                    <CostBreakdown
                      occupancies={state.occupancies}
                      pricingInfo={listing.pricingInfo.values}
                      noOfNights={noOfNights}
                    />
                  ) : null}
                </Flex>
              </LogisticsAndCostBlock>
            )}
            {isSoldOut && (
              <Box marginTop={{ _: 0, sm2: 5 }}>
                <SoldOutTag eventDate={createDateFromUnformattedString(state.searchDates.checkin)} />
              </Box>
            )}
            <DynamicMargin
              diag={listing.diag}
              otaLinkMode="fallback"
            />
          </ContentPanel>
          {!isDesktopLayout && shouldShowHotelCta ? (
            <Flex
              justifyContent={'flex-end'}
              width={'100%'}
              marginTop={5}
            >
              <HotelCTA
                listing={listing}
                redirectUrl={redirectUrl}
                sourceName={sourceName}
              />
            </Flex>
          ) : null}
        </Flex>
      </Box>
    </Container>
  );
};

const ListingEntrySkeleton = () => {
  const { isMobileOrTablet, shouldUseNewMobileLayout } = useAccommodationsListingMobileLayout();
  const isDesktopLayout = !isMobileOrTablet;

  if (shouldUseNewMobileLayout) {
    return <ListingEntryMobileV2.Skeleton />;
  }

  return (
    <Container joyPlaceId={null}>
      <ListingPhotos.Skeleton isMobileOrTablet={isMobileOrTablet} />
      <ContentPanel>
        <HeaderBlock.Skeleton isMobileOrTablet={isMobileOrTablet} />
        {!isDesktopLayout && <Divider marginY={3} />}
        {isDesktopLayout ? (
          <Flex
            flexDirection="row"
            justifyContent="space-between"
            alignItems="flex-start"
            marginTop={3}
          >
            <Flex
              flexDirection="column"
              rowGap={3}
            >
              <TravelLogistics.Skeleton />
              <UserRating.Skeleton />
            </Flex>
            <CostBreakdown.Skeleton />
          </Flex>
        ) : (
          <LogisticsAndCostBlock>
            <TravelLogistics.Skeleton />
            <UserRating.Skeleton />
            <CostBreakdown.Skeleton />
          </LogisticsAndCostBlock>
        )}
      </ContentPanel>
    </Container>
  );
};

const ListingEntry = Object.assign(React.memo(ListingEntryInner), {
  Skeleton: ListingEntrySkeleton,
});

export { ListingEntry };
